import { Loader2, PowerOff, UserPlus } from 'lucide-react';
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';

import { adminClient } from '@/api/adminClient';
import { AdminSection } from '@/components/shell/AdminSection';
import { Button } from '@/components/ui/button';
import { Dialog } from '@/components/ui/dialog';

/**
 * The organization's switch in the admin header: give yourself a seat to look
 * around (it then shows in your own org switcher).
 */
export function OrgStudioActions({ orgId, name, onChanged }: { orgId: string; name: string; onChanged: () => void }) {
  const [joining, setJoining] = useState(false);

  const addMe = async () => {
    setJoining(true);
    try {
      await adminClient.grantMyStudioSeat(orgId);
      toast.success(`You're an admin of ${name}. Switch to it from the org name in Studio's sidebar`);
      onChanged();
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Could not add you');
    } finally {
      setJoining(false);
    }
  };

  return (
    <Button type="button" size="sm" variant="outline" onClick={() => void addMe()} disabled={joining}>
      {joining ? <Loader2 className="mr-2 h-4 w-4 animate-spin" aria-hidden="true" /> : <UserPlus className="mr-2 h-4 w-4" aria-hidden="true" />}
      Add me
    </Button>
  );
}

/**
 * Turning Studio off for the whole organization, at the foot of its Overview. Only
 * platform admins see it, and the API takes it from them only.
 */
export function TurnOffStudioSection({ orgId, name }: { orgId: string; name: string }) {
  const navigate = useNavigate();
  const [confirmingOff, setConfirmingOff] = useState(false);
  const [turningOff, setTurningOff] = useState(false);

  const turnOff = async () => {
    setTurningOff(true);
    try {
      await adminClient.turnOffStudio(orgId);
      toast.success(`Studio is off for ${name}`);
      setConfirmingOff(false);
      navigate('/organizations');
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Could not turn Studio off');
    } finally {
      setTurningOff(false);
    }
  };

  return (
    <AdminSection
      title="Danger zone"
      description="Turning Studio off removes this organization from Studio: nobody in it can use Studio until it is turned back on from Add organization."
      actions={
        <Button type="button" size="sm" variant="destructive" onClick={() => setConfirmingOff(true)}>
          <PowerOff className="mr-2 h-4 w-4" aria-hidden="true" />
          Turn off Studio
        </Button>
      }
    >
      <p className="text-sm text-muted-foreground">Seats, apps and data stay. Published apps keep running.</p>
      <Dialog
        open={confirmingOff}
        onClose={() => setConfirmingOff(false)}
        title={`Turn off Studio for ${name}?`}
        description="It leaves Studio's organization list and people can no longer switch to it. Seats, apps and data stay, so turning it back on from Add organization restores everyone."
        footer={
          <>
            <Button type="button" variant="outline" onClick={() => setConfirmingOff(false)} disabled={turningOff}>
              Cancel
            </Button>
            <Button type="button" variant="destructive" onClick={() => void turnOff()} disabled={turningOff}>
              {turningOff ? <Loader2 className="mr-2 h-4 w-4 animate-spin" aria-hidden="true" /> : null}
              Turn off Studio
            </Button>
          </>
        }
      >
        <p className="text-sm text-muted-foreground">Published apps keep running. Use Projects to take them offline first if that's needed.</p>
      </Dialog>
    </AdminSection>
  );
}
